import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";

import { AppVersionContext } from "components/app-version/AppVersionContext";
import { createAppVersion, updateAppVersion, validateAppVersion } from "services/AppVersionService";
import { handleError } from "utils/handleError";
import { actionUpdate, actionView } from "utils/action";
import { Dropdown } from "primereact/dropdown";

export const AppVersionEditor = () => {
    const toast = useRef(null);
    const { t, i18n } = useTranslation(["app-version", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const typeApps = [
        { label: t("version.web"), value: 2 },
        { label: t("version.mobile"), value: 1 },
    ];

    const appVersionContext = useContext(AppVersionContext);
    const { appVersion, resetAppVersion, setAppVersion, appVersions, setAppVersions, submitted, setSubmitted, appVersionDialog, setAppVersionDialog } = appVersionContext;

    const [showSpinner, setShowSpinner] = useState(false);
    const [validForm, setValidForm] = useState(false);
    const [titleEditor, setTitleEditor] = useState(t("version.titleCreateDetail"));

    useEffect(() => {
        // create
        if (!appVersion?.id) {
            setTitleEditor(t("version.titleCreateDetail"));
        } else {
            // update
            if (actionUpdate === appVersion?.action) {
                setTitleEditor(t("version.titleUpdateDetail"));
            }
            // view
            else {
                setTitleEditor(t("version.titleDetail"));
            }
        }
    });

    const onInputChange = (e, name) => {
        const val = e.target?.value || e.value || "";
        let _appVersion = { ...appVersion };
        _appVersion[`${name}`] = val;

        setAppVersion(_appVersion);
    };

    const hideDialog = () => {
        setValidForm(false);
        setSubmitted(false);
        setAppVersionDialog(false);
    };

    const saveAppVersionAction = () => {
        setShowSpinner(true);
        setValidForm(true);
        validateAppVersion(appVersion)
            .then(() => {
                if (!appVersion?.id) {
                    // Creating
                    return createAppVersion(headers, appVersion).then((res) => {
                        toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.created", { ns: "common", object: t("version") }), life: 3000 });
                        setValidForm(false);
                        setAppVersionDialog(false);
                        setSubmitted(true);
                        resetAppVersion();
                    });
                } else {
                    // Updating
                    return updateAppVersion(headers, appVersion?.id, {
                        name: appVersion?.name,
                        description: appVersion?.description,
                        appType: appVersion?.appType,
                    }).then((res) => {
                        toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.updated", { ns: "common", object: t("version") }), life: 3000 });
                        setValidForm(false);
                        setAppVersionDialog(false);
                        setSubmitted(true);
                        resetAppVersion();
                    });
                }
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setShowSpinner(false));
    };

    const findIndexById = (id) => {
        let index = -1;
        for (let i = 0; i < appVersions.length; i++) {
            if (appVersions[i].id === id) {
                index = i;
                break;
            }
        }

        return index;
    };

    const dialogFooter = (
        <>
            <Button label={t("cancel", { ns: "common" })} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={t("save", { ns: "common" })} icon="pi pi-check" onClick={saveAppVersionAction} />
        </>
    );

    const checkView = () => {
        return actionView === appVersion?.action;
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog visible={appVersionDialog} style={{ width: "70%" }} header={titleEditor.toUpperCase()} modal className="p-fluid text-center" onHide={hideDialog}>
                <div className="formgrid">
                    <div className="grid text-left">
                        <div className="col-12">
                            <div className="grid">
                                {/* loại */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="appType">
                                        {t("version.appType")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <Dropdown id="appType" value={appVersion?.appType} options={typeApps} onChange={(e) => onInputChange(e, "appType")} disabled={checkView()} required className={classNames({ "p-invalid": validForm && !appVersion?.name })} />
                                    {validForm && !appVersion?.appType && <small className="text-danger">{t("version.appType.required")}.</small>}
                                </div>
                                {/* tên phiên bản */}
                                <div className="field col-12 md:col-9">
                                    <label htmlFor="name">
                                        {t("name")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <InputText id="name" value={appVersion?.name} maxLength={255} onChange={(e) => onInputChange(e, "name")} required className={classNames({ "p-invalid": validForm && !appVersion?.name })} readOnly={checkView()} />
                                    {validForm && !appVersion?.name && <small className="text-danger">{t("name.required")}.</small>}
                                </div>
                            </div>

                            <div className="grid">
                                <div className="field col-12">
                                    {/* mô tả */}
                                    <label htmlFor="description">{t("description")}</label>
                                    <InputTextarea id="description" readOnly={checkView()} value={appVersion?.description || ""} maxLength={500} onChange={(e) => onInputChange(e, "description")} rows={3} cols={20} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="grid">
                    <div className="col-12">
                        {/* save */}
                        {!checkView() && (
                            <Button
                                style={{
                                    width: "max-content",
                                }}
                                className="mr-2"
                                label={t("save", { ns: "common" })}
                                icon="pi pi-check"
                                onClick={saveAppVersionAction}
                            />
                        )}
                        {/* close */}
                        <Button
                            style={{
                                width: "max-content",
                            }}
                            className="mr-2 p-button-danger"
                            label={t("close", { ns: "common" })}
                            icon="pi pi-times"
                            onClick={hideDialog}
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog visible={showSpinner} closable={false} resizable={false} draggable={false} showHeader={false}>
                <ProgressSpinner />
            </Dialog>
        </>
    );
};
